import type { AiTone, OfferingType } from '@cartiq/shared';

export interface DraftProduct {
  id: string;
  name: string;
  price: string;
  currency: string;
  type: OfferingType;
}

/** Deliberate allowlist. No identity, contact, payout or bank fields belong here. */
export interface WizardDraft {
  step?: number;
  storeName?: string;
  logoUrl?: string;
  primary?: string;
  accent?: string;
  products?: DraftProduct[];
  personaName?: string;
  aiTone?: AiTone;
}

export function draftKey(kind: string, workspaceId?: string, userId?: string, contextKey = 'standalone'): string | null {
  if (!workspaceId || !userId || !contextKey) return null;
  return `cartiq:${kind}:v2:${encodeURIComponent(JSON.stringify([workspaceId, userId, contextKey]))}`;
}

export function removeLegacyWizardDraft(storage?: Storage): void {
  try {
    (storage ?? localStorage).removeItem('cartiq:onboarding');
  } catch { /* Private/denied storage must not prevent opening the app. */ }
}

export function sanitizeWizardDraft(value: unknown): WizardDraft {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const source = value as Record<string, unknown>;
  const out: WizardDraft = {};
  for (const key of ['storeName', 'logoUrl', 'personaName'] as const) {
    if (typeof source[key] === 'string' && source[key].length <= 2048) out[key] = source[key];
  }
  for (const key of ['primary', 'accent'] as const) {
    if (typeof source[key] === 'string' && /^#[0-9a-f]{6}$/i.test(source[key])) out[key] = source[key];
  }
  if (typeof source.step === 'number' && Number.isInteger(source.step) && source.step >= 0 && source.step < 5) {
    out.step = source.step;
  }
  if (source.aiTone === 'balanced' || source.aiTone === 'formal' || source.aiTone === 'casual') {
    out.aiTone = source.aiTone;
  }
  if (Array.isArray(source.products)) {
    out.products = source.products.slice(0, 100).flatMap((value: unknown) => {
      if (!value || typeof value !== 'object') return [];
      const p = value as Record<string, unknown>;
      if (!['id', 'name', 'price', 'currency'].every((k) => typeof p[k] === 'string' && (p[k] as string).length <= 2048) ||
          (p.type !== 'product' && p.type !== 'service')) return [];
      return [{ id: p.id as string, name: p.name as string, price: p.price as string, currency: p.currency as string, type: p.type }];
    });
  }
  return out;
}

export function readWizardDraft(key: string | null, storage?: Storage): WizardDraft {
  removeLegacyWizardDraft(storage);
  if (!key) return {};
  try {
    const raw = (storage ?? localStorage).getItem(key);
    return raw ? sanitizeWizardDraft(JSON.parse(raw)) : {};
  } catch { return {}; }
}

export function writeWizardDraft(key: string | null, value: unknown, storage?: Storage): void {
  removeLegacyWizardDraft(storage);
  if (!key) return;
  try { (storage ?? localStorage).setItem(key, JSON.stringify(sanitizeWizardDraft(value))); }
  catch { /* Best-effort, non-sensitive draft only. */ }
}
